import { BaseScreen } from '@goodboydigital/astro';
import { Sprite, Texture } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { RaceMap } from '../ui/RaceMap';

export class PreRaceScreen extends BaseScreen
{
    public app: App;
    public manifests: string[] = [];
    public bg!: Sprite;
    public readyText!: LocalisedText;
    public bonusText!: LocalisedText;
    public readyButton!: NineSliceButton;
    public backButton!: NineSliceButton;
    public raceMap!: RaceMap;

    constructor(app: App)
    {
        super(app);
        this.app = app;
    }

    async init(): Promise<void>
    {
        this.bg = Sprite.from(Texture.WHITE);
        this.bg.alpha = 0;
        this.bg.width = this.bg.height = 9999;
        this.bg.anchor.set(0.5);
        this.view.addChild(this.bg);

        this.raceMap = new RaceMap();
        this.view.addChild(this.raceMap.view);
        this.raceMap.onTapSelected.connect((race: number) => this.play(race));

        this.bonusText = new LocalisedText('preRaceScreen_bonus', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 48,
            maxWidth: 300,
        });

        this.bonusText.anchor.set(0.5);
        const adIcon = Sprite.from(Shared.assets.getTexture('video-ad'));

        adIcon.anchor.set(0.5);
        adIcon.scale.set(1.3);
        adIcon.x += 155;
        adIcon.y -= 50;
        adIcon.rotation = Math.PI * 20 / 180;

        const readyIcon = new Sprite();

        this.readyText = new LocalisedText('preRaceScreen_play', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 10,
            lineJoin: 'round',
            align: 'center',
            fontSize: 56,
            maxWidth: 300,
        });

        this.readyText.anchor.set(0.5);
        readyIcon.addChild(this.readyText);

        this.readyButton = new NineSliceButton({
            icon: readyIcon,
            bgAsset: 'button-ninesliced-yellow',
            w: 350,
            h: 130,
        });
        this.view.addChild(this.readyButton.view);
        this.readyButton.onPress.connect(() => this.play());

        const backIcon = Sprite.from(Shared.assets.getTexture('icon-home'));

        this.backButton = new NineSliceButton({
            icon: backIcon,
            bgAsset: 'button-ninesliced-yellow',
            accessibleTitle: 'Back',
            w: backIcon.width * 1.5,
            h: backIcon.height * 1.5,
        });
        this.view.addChild(this.backButton.view);
        this.backButton.onPress.connect(() =>
        {
            this.app.controller.toHome();
        });
    }

    play(race?: number): void
    {
        if (!race) race = this.raceMap.getIndex() + 1;

        this.app.controller.toGameplay(race);
    }

    show(): void
    {
        this.view.visible = true;
        this.resize(this.width, this.height);
        
        this.app.analytics.trackState(AnalyticsState.RACE_SELECT);

        // Unlock all races if in rec mode
        this.raceMap.open();
        this.raceMap.setMaxIndex(Settings.rec || Settings.race >= 0 ? Settings.maxRaces : Shared.user.numRacesPlayed());
        this.raceMap.setIndex((Settings.race >= 0 ? Settings.race : Shared.user.data.race) - 1);
        this.readyButton.view.visible = false;

        Shared.game.soundSystem.playRaceMusic();

        this.readyButton.show(true);
    }

    async hide(): Promise<void>
    {
        await this.readyButton.hide(true);
        await this.raceMap.close();
    }

    hidden(): void
    {
        this.view.visible = false;
    }

    update(delta = 1): void
    {
        if (this.raceMap) this.raceMap.update(delta);
    }

    resize(w: number, h: number): void
    {
        super.resize(w, h);
        this.view.x = (this.width - this.app.width) * 0.5;
        this.view.y = (this.height - this.app.height) * 0.5;
        this.bg.position.copyFrom(this.app.stageCenter);

        this.readyButton.view.position.set(
            this.app.stageCenter.x,
            this.app.stageBottom - 90);

        this.backButton.view.position.set(
            this.app.stageLeft + 90,
            this.app.stageTop + 90,
        );

        this.raceMap.view.position.set(
            this.app.stageCenter.x,
            this.app.stageCenter.y + 75,
        );
    }
}
